import { useEffect, useState } from "react";
import api, { euro, apiError, pctIt, ptIt } from "@/lib/api";
import { SectionTitle, Progress, KpiCard } from "@/components/common";
import { Modal, Row, Field } from "@/pages/Notizie";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Trophy, Medal, Target, TrendingUp, Award, Users, Crown, LineChart as LineIcon, Pencil, Download, FileText, FileSpreadsheet, Gift, RotateCcw, Archive } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";

const MEDALS = ["#FFD700", "#C0C0C0", "#CD7F32"];
const BAR_COLORS = ["#FC82ED", "#7C3AED", "#0EA5E9", "#F59E0B", "#10B981"];
const POS = ["primo", "secondo", "terzo"];

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

export default function Classifica() {
  const { user, isDirezione } = useAuth();
  const [rows, setRows] = useState([]);
  const [obiettivi, setObiettivi] = useState([]);
  const [me, setMe] = useState(null);
  const [teamFilter, setTeamFilter] = useState("");
  const [trend, setTrend] = useState(null);
  const [edit, setEdit] = useState(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [premio, setPremio] = useState(null);
  const [storico, setStorico] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [grafico, setGrafico] = useState(null);
  const [archivio, setArchivio] = useState([]);
  const [premioModal, setPremioModal] = useState(false);
  const [archivioOpen, setArchivioOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const loadClassifica = (team) => api.get("/classifica", { params: team ? { team } : {} }).then((r) => setRows(r.data));
  const loadObiettivi = () => api.get("/obiettivi").then((r) => setObiettivi(r.data)).catch(() => {});
  const loadContest = () => {
    loadClassifica(teamFilter);
    api.get("/contest/mensile").then((r) => setPremio(r.data)).catch(() => {});
    api.get("/contest/storico", { params: { mesi: 6 } }).then((r) => setStorico(r.data)).catch(() => {});
    api.get("/contest/settings").then((r) => setCfg(r.data)).catch(() => {});
    api.get("/contest/storico-grafico", { params: { mesi: 6, top: 5 } }).then((r) => setGrafico(r.data)).catch(() => {});
    api.get("/contest/archivio").then((r) => setArchivio(r.data)).catch(() => {});
  };
  useEffect(() => { loadClassifica(teamFilter); }, [teamFilter]);
  useEffect(() => {
    loadObiettivi();
    api.get("/performance/me").then((r) => setMe(r.data)).catch(() => {});
    loadContest();
  }, []);

  const resetStagione = async () => {
    if (!window.confirm("Azzerare la stagione? La classifica attuale verrà archiviata e i punti contest totali ripartiranno da zero dalle prossime vendite/incarichi.")) return;
    setResetting(true);
    try { const r = await api.post("/contest/reset-stagione"); toast.success(`Stagione azzerata · ${r.data.archiviati} agenti archiviati`); loadContest(); }
    catch (e) { toast.error(apiError(e)); } finally { setResetting(false); }
  };

  const exportCSV = (expRows) => {
    const header = ["Posizione", "Agente", "Team", "Fatturato mese", "Incarichi", "Vendite", "Appuntamenti", "Conversion %"];
    const lines = expRows.map((a, i) => [i + 1, a.nome, a.team || "", a.fatturato_mese, a.incarichi, a.vendite, a.appuntamenti, a.conversion_rate]);
    const csv = [header, ...lines].map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "classifica.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const exportPDF = (expRows, expOb) => {
    const rk = expRows.map((a, i) => `<tr><td>${i + 1}</td><td>${esc(a.nome)}</td><td>${esc(a.team || "")}</td><td style="text-align:right">${euro(a.fatturato_mese)}</td><td style="text-align:right">${a.incarichi}</td><td style="text-align:right">${a.conversion_rate}%</td></tr>`).join("");
    const obRows = (expOb || []).map((o) => `<tr><td>${esc(o.nome)}</td><td>${esc(o.team || "")}</td><td style="text-align:right">${euro(o.fatturato_mese)} / ${euro(o.obiettivo_mese)}</td><td style="text-align:right">${o.percentuale}%</td></tr>`).join("");
    const obBlock = (expOb && expOb.length) ? `<h3>Obiettivi</h3><table><tr><th>Agente</th><th>Team</th><th>Fatturato / Obiettivo</th><th>%</th></tr>${obRows}</table>` : "";
    const html = `<html><head><title>Classifica IMMOBILART</title><style>body{font-family:Arial;padding:24px;color:#111}h1{color:#FC82ED}table{width:100%;border-collapse:collapse;margin-bottom:24px}th,td{border-bottom:1px solid #eee;padding:8px;text-align:left;font-size:13px}</style></head><body><h1>IMMOBILART · Classifica &amp; Obiettivi</h1><h3>Classifica</h3><table><tr><th>#</th><th>Agente</th><th>Team</th><th>Fatturato</th><th>Incarichi</th><th>Conv.</th></tr>${rk}</table>${obBlock}</body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const w = window.open(url, "_blank");
    if (w) w.onload = () => { w.focus(); w.print(); setTimeout(() => URL.revokeObjectURL(url), 1000); };
  };
  const exportXLSX = (expRows, expOb) => {
    const rk = expRows.map((a, i) => `<tr><td>${i + 1}</td><td>${esc(a.nome)}</td><td>${esc(a.team || "")}</td><td>${a.fatturato_mese}</td><td>${a.incarichi}</td><td>${a.vendite}</td><td>${a.appuntamenti}</td><td>${pctIt(a.conversion_rate)}</td></tr>`).join("");
    const obRows = (expOb || []).map((o) => `<tr><td>${esc(o.nome)}</td><td>${esc(o.team || "")}</td><td>${o.fatturato_mese}</td><td>${o.obiettivo_mese}</td><td>${pctIt(o.percentuale)}</td></tr>`).join("");
    const obBlock = (expOb && expOb.length) ? `<br/><table border="1"><tr><th>Agente</th><th>Team</th><th>Fatturato</th><th>Obiettivo</th><th>%</th></tr>${obRows}</table>` : "";
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1"><tr><th>#</th><th>Agente</th><th>Team</th><th>Fatturato</th><th>Incarichi</th><th>Vendite</th><th>Appuntamenti</th><th>Conv%</th></tr>${rk}</table>${obBlock}</body></html>`;
    const blob = new Blob(["\ufeff" + html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "classifica.xls"; a.click(); URL.revokeObjectURL(url);
  };
  const exportContestCSV = (r) => {
    const header = ["Posizione", "Agente", "Team", "Punti incarichi", "Punti vendite", "Totale punti", "Incarichi", "Vendite"];
    const lines = r.map((a, i) => [i + 1, a.nome, a.team || "", a.punti_incarichi, a.punti_vendite, a.punti_totali, a.incarichi, a.vendite]);
    let csv = [header, ...lines].map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    if (storico.length) csv += "\n\nStorico vincitori\nMese,Vincitore,Punti\n" + storico.map((s) => `"${s.mese}","${s.vincitore ? s.vincitore.nome : "-"}","${ptIt(s.vincitore ? s.vincitore.punti : 0)}"`).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "contest_punti.csv"; a.click(); URL.revokeObjectURL(url);
  };
  const exportContestXLSX = (r) => {
    const rk = r.map((a, i) => `<tr><td>${i + 1}</td><td>${esc(a.nome)}</td><td>${esc(a.team || "")}</td><td>${ptIt(a.punti_incarichi)}</td><td>${ptIt(a.punti_vendite)}</td><td>${ptIt(a.punti_totali)}</td><td>${a.incarichi}</td><td>${a.vendite}</td></tr>`).join("");
    const st = storico.map((s) => `<tr><td>${esc(s.mese)}</td><td>${esc(s.vincitore ? s.vincitore.nome : "-")}</td><td>${ptIt(s.vincitore ? s.vincitore.punti : 0)}</td></tr>`).join("");
    const stBlock = storico.length ? `<br/><table border="1"><tr><th>Mese</th><th>Vincitore</th><th>Punti</th></tr>${st}</table>` : "";
    const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel"><head><meta charset="utf-8"></head><body><table border="1"><tr><th>#</th><th>Agente</th><th>Team</th><th>Punti incarichi</th><th>Punti vendite</th><th>Totale</th><th>Incarichi</th><th>Vendite</th></tr>${rk}</table>${stBlock}</body></html>`;
    const blob = new Blob(["\ufeff" + html], { type: "application/vnd.ms-excel" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = "contest_punti.xls"; a.click(); URL.revokeObjectURL(url);
  };
  const exportContestPDF = (r) => {
    const rk = r.map((a, i) => `<tr><td>${i + 1}</td><td>${esc(a.nome)}</td><td style="text-align:right">${ptIt(a.punti_incarichi)}</td><td style="text-align:right">${ptIt(a.punti_vendite)}</td><td style="text-align:right"><b>${ptIt(a.punti_totali)}</b></td></tr>`).join("");
    const st = storico.map((s) => `<tr><td>${esc(s.mese)}</td><td>${esc(s.vincitore ? s.vincitore.nome : "-")}</td><td style="text-align:right">${ptIt(s.vincitore ? s.vincitore.punti : 0)}</td></tr>`).join("");
    const stBlock = storico.length ? `<h3>Storico vincitori</h3><table><tr><th>Mese</th><th>Vincitore</th><th>Punti</th></tr>${st}</table>` : "";
    const html = `<html><head><title>Contest punti IMMOBILART</title><style>body{font-family:Arial;padding:24px;color:#111}h1{color:#FC82ED}table{width:100%;border-collapse:collapse;margin-bottom:24px}th,td{border-bottom:1px solid #eee;padding:8px;text-align:left;font-size:13px}</style></head><body><h1>IMMOBILART · Contest a punti</h1><h3>Classifica punti</h3><table><tr><th>#</th><th>Agente</th><th>Punti incarichi</th><th>Punti vendite</th><th>Totale</th></tr>${rk}</table>${stBlock}</body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
    const w = window.open(url, "_blank"); if (w) w.onload = () => { w.focus(); w.print(); setTimeout(() => URL.revokeObjectURL(url), 1000); };
  };

  const teams = [...new Set(obiettivi.map((o) => o.team).filter(Boolean))];
  const monthMap = {};
  (premio?.agenti || []).forEach((a) => { monthMap[a.agent_id] = a; });
  const topFatturato = rows[0];
  const topIncarichi = rows.reduce((best, a) => (!best || a.incarichi > best.incarichi ? a : best), null);
  const teamLabel = isDirezione ? "Tutti i team" : (user?.team_name || "Il tuo team");

  return (
    <div className="fade-up">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <SectionTitle sub="Obiettivi del proprio team e classifica produttiva globale. I dati personali dei clienti restano privati.">Obiettivi &amp; Classifica</SectionTitle>
        <button data-testid="export-btn" onClick={() => setExportOpen(true)} className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-white border border-[var(--border)] text-slate-700 font-semibold text-sm hover:border-[#FC82ED]/40 transition-all"><Download size={15} /> Esporta</button>
      </div>

      {me && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard testid="perf-obiettivo" label="Obiettivo Mese" value={euro(me.obiettivo_mese)} icon={Target} />
          <KpiCard testid="perf-fatturato" label="Fatturato Mese" value={euro(me.fatturato_mese)} icon={TrendingUp} accent />
          <KpiCard testid="perf-pipeline" label="Pipeline" value={euro(me.pipeline)} icon={TrendingUp} />
          <KpiCard testid="perf-conversion" label="Conversion Rate" value={pctIt(me.conversion_rate)} icon={Award} accent />
        </div>
      )}

      {/* ---------------- OBIETTIVI (team-scoped) ---------------- */}
      <div data-testid="obiettivi-section" className="card-immo overflow-hidden mb-8">
        <div className="p-4 border-b border-[var(--border)] flex items-center gap-2 font-display font-bold">
          <Target className="text-[#FC82ED]" size={18} /> Obiettivi
          <span className="text-xs font-medium text-[var(--muted)]">· {teamLabel}</span>
        </div>
        {obiettivi.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--muted)] flex flex-col items-center gap-2">
            <Users size={26} className="text-[var(--muted)]" />
            Nessun obiettivo disponibile per il tuo team.
          </div>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {obiettivi.map((o) => {
              const isMe = o.agent_id === user?.id;
              return (
                <div key={o.agent_id} data-testid={`obiettivo-${o.agent_id}`} className={`p-4 flex items-center gap-4 flex-wrap ${isMe ? "bg-[#FC82ED]/[0.06]" : ""}`}>
                  {o.avatar ? <img src={o.avatar} className="w-10 h-10 rounded-full object-cover border border-[var(--border)]" alt="" /> :
                    <div className="w-10 h-10 rounded-full bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] font-bold text-sm shrink-0">{o.nome?.slice(0, 1)}</div>}
                  <div className="flex-1 min-w-[140px]">
                    <div className="font-semibold text-sm flex items-center gap-2">{o.nome} {isMe && <span className="text-[10px] bg-[#FC82ED] text-black px-1.5 py-0.5 rounded font-bold">TU</span>}</div>
                    {isDirezione && <div className="text-xs text-[var(--muted)]">{o.team || "—"}</div>}
                  </div>
                  <div className="w-52 shrink-0">
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="mono font-semibold">{euro(o.fatturato_mese)} / {euro(o.obiettivo_mese)}</span>
                      <span className={o.percentuale >= 100 ? "text-emerald-500 font-bold" : "text-[#FC82ED] font-semibold"}>{pctIt(o.percentuale)}</span>
                    </div>
                    <Progress value={o.percentuale} />
                    <div className="text-[10px] text-[var(--muted)] mt-1.5">Anno: {euro(o.fatturato_anno)} / {euro(o.obiettivo_anno)} · {pctIt(o.percentuale_anno)}</div>
                  </div>
                  <button data-testid={`trend-${o.agent_id}`} onClick={() => setTrend(o)}
                          className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[#FC82ED] transition-colors shrink-0">
                    <LineIcon size={14} /> Trend
                  </button>
                  {isDirezione && (
                    <button data-testid={`edit-obj-${o.agent_id}`} onClick={() => setEdit(o)}
                            className="flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-[#FC82ED] transition-colors shrink-0">
                      <Pencil size={13} /> Modifica
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ---------------- CLASSIFICA (global) ---------------- */}
      {isDirezione && teams.length > 1 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap" data-testid="team-filter">
          <span className="text-xs font-semibold uppercase tracking-wide text-[var(--muted)]">Confronta:</span>
          {[["", "Globale"], ...teams.map((t) => [t, t])].map(([k, l]) => (
            <button key={k || "all"} data-testid={`team-chip-${k || "globale"}`} onClick={() => setTeamFilter(k)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-semibold border transition-all ${teamFilter === k ? "bg-[#FC82ED] text-black border-[#FC82ED]" : "border-[var(--border)] text-slate-600 hover:border-[#FC82ED]/40"}`}>
              {l}
            </button>
          ))}
        </div>
      )}

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <KpiCard testid="winner-periodo" label="Vincitore del periodo" value={topFatturato?.nome || "—"} sub={topFatturato ? euro(topFatturato.fatturato_mese) : ""} icon={Crown} accent />
        <KpiCard testid="top-fatturato" label="Miglior Fatturato" value={topFatturato ? euro(topFatturato.fatturato_mese) : "—"} sub={topFatturato?.nome} icon={TrendingUp} />
        <KpiCard testid="top-incarichi" label="Più Incarichi" value={topIncarichi ? topIncarichi.incarichi : "—"} sub={topIncarichi?.nome} icon={Award} />
      </div>

      {premio && (
        <div data-testid="premio-mese" className="card-immo p-5 mb-4 border-amber-300/50 bg-gradient-to-br from-amber-50 to-white">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-12 h-12 rounded-2xl bg-amber-400/20 border border-amber-300 flex items-center justify-center text-amber-500 shrink-0"><Crown size={26} /></div>
            <div className="flex-1 min-w-[180px]">
              <div className="text-[11px] font-bold uppercase tracking-wide text-amber-600">Premio del mese · punti contest</div>
              {premio.vincitore ? (
                <div className="font-display font-extrabold text-lg flex items-center gap-2 flex-wrap">{premio.vincitore.nome}
                  <span className="inline-flex items-center gap-1 text-sm text-amber-600"><Trophy size={15} /> {ptIt(premio.vincitore.punti_totali)} pt</span>
                </div>
              ) : <div className="text-sm text-[var(--muted)] mt-0.5">Nessun punto ancora assegnato questo mese.</div>}
              {cfg?.premio_mese && <div data-testid="premio-descrizione" className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-amber-700"><Gift size={14} /> In palio: {cfg.premio_mese}</div>}
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              {premio.vincitore && <div className="text-right text-xs text-[var(--muted)]">Incarichi {ptIt(premio.vincitore.punti_incarichi)} pt · Vendite {ptIt(premio.vincitore.punti_vendite)} pt</div>}
              {isDirezione && (
                <div className="flex gap-2 flex-wrap justify-end">
                  <button data-testid="premio-edit-btn" onClick={() => setPremioModal(true)} className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:underline"><Gift size={13} /> Premio</button>
                  <button data-testid="archivio-btn" onClick={() => setArchivioOpen(true)} className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:underline"><Archive size={13} /> Archivio</button>
                  <button data-testid="reset-stagione-btn" disabled={resetting} onClick={resetStagione} className="inline-flex items-center gap-1 text-xs font-medium text-red-500 hover:underline disabled:opacity-50"><RotateCcw size={13} /> Azzera stagione</button>
                </div>
              )}
            </div>
          </div>
          {cfg?.season_start && <div className="text-[11px] text-[var(--muted)] mt-2">Stagione iniziata il {cfg.season_start} · i punti contest totali contano da questa data.</div>}
          {premio.agenti && premio.agenti.filter((a) => a.punti_totali > 0).length > 0 && (
            <div data-testid="podio-mese" className="mt-4 pt-4 border-t border-amber-200/60">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--muted)] mb-2">Podio del mese</div>
              <div className="grid sm:grid-cols-3 gap-3">
                {premio.agenti.filter((a) => a.punti_totali > 0).slice(0, 3).map((a, i) => (
                  <div key={a.agent_id} data-testid={`podio-${i + 1}`} className="rounded-xl border p-3 flex items-center gap-3 bg-white" style={{ borderColor: MEDALS[i] + "66" }}>
                    <Medal size={22} style={{ color: MEDALS[i] }} />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm truncate">{a.nome}</div>
                      <div className="mono text-xs text-amber-600 font-bold">{ptIt(a.punti_totali)} pt</div>
                      {cfg?.premi?.[POS[i]] && <div className="text-[11px] text-amber-700 inline-flex items-center gap-1 truncate"><Gift size={11} /> {cfg.premi[POS[i]]}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {grafico?.mesi?.length > 0 && grafico.agenti.length > 0 && (
            <div className="mt-4 pt-4 border-t border-amber-200/60">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--muted)] mb-2">Punti mensili · top {grafico.agenti.length} agenti</div>
              <div data-testid="contest-grafico" style={{ width: "100%", height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={grafico.mesi} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0e6d0" vertical={false} />
                    <XAxis dataKey="mese" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {grafico.agenti.map((nome, i) => <Bar key={nome} dataKey={nome} fill={BAR_COLORS[i % BAR_COLORS.length]} radius={[3, 3, 0, 0]} />)}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {storico.length > 0 && (
            <div className="mt-4 pt-4 border-t border-amber-200/60">
              <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--muted)] mb-2">Storico vincitori (ultimi mesi)</div>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {storico.map((s) => (
                  <div key={s.mese} data-testid={`premio-storico-${s.mese}`} className="min-w-[120px] rounded-xl border border-[var(--border)] p-3 text-center shrink-0 bg-white">
                    <div className="text-[10px] uppercase text-[var(--muted)]">{s.mese}</div>
                    <div className="font-semibold text-sm truncate">{s.vincitore ? s.vincitore.nome : "—"}</div>
                    <div className="mono text-amber-600 font-bold text-sm">{s.vincitore ? `${ptIt(s.vincitore.punti)} pt` : "0 pt"}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      {premioModal && <ContestSettingsModal cfg={cfg} onClose={() => setPremioModal(false)} onSaved={() => { setPremioModal(false); loadContest(); }} />}
      {archivioOpen && <ArchivioModal items={archivio} onClose={() => setArchivioOpen(false)} />}
      <div data-testid="contest-section" className="card-immo overflow-hidden mb-4">
        <div className="p-4 border-b border-[var(--border)] flex items-center gap-2 font-display font-bold"><Award className="text-[#FC82ED]" size={18} /> Contest · Punteggi</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-[var(--muted)] border-b border-[var(--border)]">
                <th className="p-3">#</th><th className="p-3">Agente</th>
                <th className="p-3 text-right">Punti incarichi</th><th className="p-3 text-right">Punti vendite</th>
                <th className="p-3 text-right">Totale</th><th className="p-3 text-right">Incarichi</th><th className="p-3 text-right">Vendite</th>
                <th className="p-3 text-right">Obiettivo mese</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a, i) => (
                <tr key={a.agent_id} data-testid={`contest-row-${a.agent_id}`} className={`border-b border-[var(--border)] last:border-0 ${a.agent_id === user?.id ? "bg-[#FC82ED]/[0.06]" : ""}`}>
                  <td className="p-3 mono text-[var(--muted)]">{i + 1}</td>
                  <td className="p-3 font-semibold">{a.nome}{isDirezione && a.team ? <span className="text-xs text-[var(--muted)] font-normal"> · {a.team}</span> : null}{a.agent_id === user?.id && <span className="ml-2 text-[10px] bg-[#FC82ED] text-black px-1.5 py-0.5 rounded font-bold">TU</span>}{premio?.vincitore?.agent_id === a.agent_id && <Crown size={13} className="inline text-amber-500 ml-1" title="Vincitore del mese" />}
                    {Object.keys(a.voci || {}).length > 0 && <div data-testid={`classifica-voci-${a.agent_id}`} className="text-[10px] font-normal text-[var(--muted)]">{Object.entries(a.voci).filter(([, p]) => p).map(([n, p]) => `${n} ${ptIt(p)} pt`).join(" · ")}</div>}</td>
                  <td className="p-3 text-right mono">{ptIt(a.punti_incarichi)}</td>
                  <td className="p-3 text-right mono">{ptIt(a.punti_vendite)}</td>
                  <td className="p-3 text-right mono font-extrabold text-[#FC82ED]">{ptIt(a.punti_totali)}</td>
                  <td className="p-3 text-right mono">{a.incarichi}</td>
                  <td className="p-3 text-right mono">{a.vendite}</td>
                  <td className="p-3">{(() => {
                    const ma = monthMap[a.agent_id];
                    if (!ma) return <span className="text-xs text-[var(--muted)]">—</span>;
                    const p = Math.min(100, ma.pct_punti || 0);
                    return (
                      <div className="w-28 ml-auto" data-testid={`contest-obiettivo-${a.agent_id}`}>
                        <div className="flex justify-between text-[10px] mb-0.5">
                          <span className="mono">{ptIt(ma.punti_totali)}/{ptIt(ma.obiettivo_punti)} pt</span>
                          <span className={p >= 100 ? "text-emerald-500 font-bold" : "text-[#FC82ED]"}>{pctIt(ma.pct_punti)}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#FC82ED]" style={{ width: `${p}%` }} /></div>
                      </div>
                    );
                  })()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div data-testid="classifica-section" className="card-immo overflow-hidden">
        <div className="p-4 border-b border-[var(--border)] flex items-center gap-2 font-display font-bold"><Trophy className="text-[#FC82ED]" size={18} /> Classifica Mensile <span className="text-xs font-medium text-[var(--muted)]">· {teamFilter || "Globale"}</span></div>
        <div className="divide-y divide-[var(--border)]">
          {rows.map((a, i) => {
            const isMe = a.agent_id === user?.id;
            return (
              <div key={a.agent_id} data-testid={`rank-${a.agent_id}`} className={`p-4 flex items-center gap-4 flex-wrap ${isMe ? "bg-[#FC82ED]/[0.06]" : ""}`}>
                <div className="w-8 text-center shrink-0">
                  {i < 3 ? <Medal size={22} style={{ color: MEDALS[i] }} className="mx-auto" /> : <span className="mono font-bold text-[var(--muted)]">{i + 1}</span>}
                </div>
                {a.avatar ? <img src={a.avatar} className="w-10 h-10 rounded-full object-cover border border-[var(--border)]" alt="" /> :
                  <div className="w-10 h-10 rounded-full bg-[#FC82ED]/15 border border-[#FC82ED]/30 flex items-center justify-center text-[#FC82ED] font-bold text-sm shrink-0">{a.nome?.slice(0, 1)}</div>}
                <div className="flex-1 min-w-[140px]">
                  <div className="font-semibold text-sm flex items-center gap-2">{a.nome} {isMe && <span className="text-[10px] bg-[#FC82ED] text-black px-1.5 py-0.5 rounded font-bold">TU</span>}</div>
                  <div className="text-xs text-[var(--muted)]">{a.team || "—"}</div>
                </div>
                <div className="w-32 shrink-0 text-right">
                  <div className="mono font-extrabold text-[#FC82ED]">{euro(a.fatturato_mese)}</div>
                  <div className="text-[10px] uppercase text-[var(--muted)]">Fatturato mese</div>
                </div>
                <div className="flex gap-4 text-center shrink-0">
                  <Stat n={a.incarichi} l="Incarichi" /><Stat n={a.vendite} l="Vendite" /><Stat n={a.appuntamenti} l="Appunt." /><Stat n={pctIt(a.conversion_rate)} l="Conv." />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {trend && <TrendModal agent={trend} onClose={() => setTrend(null)} />}
      {edit && <ObiettivoEditModal o={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); loadObiettivi(); }} />}
      {exportOpen && <ExportModal teams={teams} isDirezione={isDirezione} onCSV={exportCSV} onPDF={exportPDF} onXLSX={exportXLSX} onContestCSV={exportContestCSV} onContestXLSX={exportContestXLSX} onContestPDF={exportContestPDF} onClose={() => setExportOpen(false)} />}
    </div>
  );
}

function ExportModal({ teams, isDirezione, onCSV, onPDF, onXLSX, onContestCSV, onContestXLSX, onContestPDF, onClose }) {
  const [team, setTeam] = useState("");
  const [withObj, setWithObj] = useState(true);
  const [busy, setBusy] = useState(false);
  const run = async (fmt) => {
    setBusy(true);
    try {
      const rc = await api.get("/classifica", { params: team ? { team } : {} });
      let ob = [];
      if (withObj) { const ro = await api.get("/obiettivi"); ob = team ? ro.data.filter((o) => o.team === team) : ro.data; }
      if (fmt === "csv") onCSV(rc.data); else if (fmt === "xlsx") onXLSX(rc.data, ob); else onPDF(rc.data, ob);
      onClose();
    } finally { setBusy(false); }
  };
  const runContest = async (fmt) => {
    setBusy(true);
    try {
      const rc = await api.get("/classifica", { params: team ? { team } : {} });
      if (fmt === "csv") onContestCSV(rc.data); else if (fmt === "xlsx") onContestXLSX(rc.data); else onContestPDF(rc.data);
      onClose();
    } finally { setBusy(false); }
  };
  return (
    <Modal title="Esporta Classifica" onClose={onClose} onSave={onClose} saveLabel="Chiudi" testid="export-modal">
      <div className="space-y-3">
        {isDirezione && (
          <div>
            <label className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)]">Team</label>
            <select data-testid="export-team" value={team} onChange={(e) => setTeam(e.target.value)} className="w-full mt-1 px-3 py-2.5 rounded-lg bg-white border border-[var(--border)] text-sm focus:border-[#FC82ED] outline-none">
              <option value="">Tutti i team</option>
              {teams.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        )}
        <label data-testid="export-with-obiettivi" className="flex items-center gap-2 text-sm p-2 rounded-lg border border-[var(--border)] cursor-pointer">
          <input type="checkbox" checked={withObj} onChange={(e) => setWithObj(e.target.checked)} /> Includi gli obiettivi nel PDF
        </label>
        <div className="flex gap-2 pt-1">
          <button data-testid="export-csv" disabled={busy} onClick={() => run("csv")} className="flex-1 py-2.5 rounded-xl bg-[#FC82ED] text-black font-semibold text-sm glow-pink flex items-center justify-center gap-2 disabled:opacity-50"><Download size={15} /> CSV</button>
          <button data-testid="export-xlsx" disabled={busy} onClick={() => run("xlsx")} className="flex-1 py-2.5 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"><FileSpreadsheet size={15} /> Excel</button>
          <button data-testid="export-pdf" disabled={busy} onClick={() => run("pdf")} className="flex-1 py-2.5 rounded-xl bg-white border border-[var(--border)] text-slate-700 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"><FileText size={15} /> PDF</button>
        </div>
        <div className="pt-3 mt-1 border-t border-[var(--border)]">
          <div className="text-[11px] font-bold uppercase tracking-wide text-[var(--muted)] mb-2">Contest a punti (classifica punti + storico vincitori)</div>
          <div className="flex gap-2">
            <button data-testid="export-contest-csv" disabled={busy} onClick={() => runContest("csv")} className="flex-1 py-2.5 rounded-xl bg-amber-50 text-amber-600 border border-amber-200 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"><Download size={15} /> CSV</button>
            <button data-testid="export-contest-xlsx" disabled={busy} onClick={() => runContest("xlsx")} className="flex-1 py-2.5 rounded-xl bg-emerald-50 text-emerald-600 border border-emerald-200 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"><FileSpreadsheet size={15} /> Excel</button>
            <button data-testid="export-contest-pdf" disabled={busy} onClick={() => runContest("pdf")} className="flex-1 py-2.5 rounded-xl bg-white border border-[var(--border)] text-slate-700 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-50"><FileText size={15} /> PDF</button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function ContestSettingsModal({ cfg, onClose, onSaved }) {
  const [primo, setPrimo] = useState(cfg?.premi?.primo || "");
  const [secondo, setSecondo] = useState(cfg?.premi?.secondo || "");
  const [terzo, setTerzo] = useState(cfg?.premi?.terzo || "");
  const [autoOn, setAutoOn] = useState(!!cfg?.auto_reset?.enabled);
  const [giorno, setGiorno] = useState(cfg?.auto_reset?.giorno || "12-31");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/contest/settings", { premi: { primo, secondo, terzo }, auto_reset: { enabled: autoOn, giorno } });
      toast.success("Impostazioni contest aggiornate"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title="Impostazioni contest" testid="premio-modal" onClose={onClose} onSave={save} saving={saving}>
      <Field label="Premio 1° posto (es. Buono 200€)" testid="premio-input" value={primo} onChange={(e) => setPrimo(e.target.value)} />
      <Field label="Premio 2° posto" testid="premio-2" value={secondo} onChange={(e) => setSecondo(e.target.value)} />
      <Field label="Premio 3° posto" testid="premio-3" value={terzo} onChange={(e) => setTerzo(e.target.value)} />
      <div className="pt-2 border-t border-[var(--border)] space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium cursor-pointer"><input type="checkbox" data-testid="auto-reset-toggle" checked={autoOn} onChange={(e) => setAutoOn(e.target.checked)} /> Azzeramento automatico annuale della stagione</label>
        {autoOn && <Field label="Giorno del reset (MM-GG, es. 12-31)" testid="auto-reset-giorno" value={giorno} onChange={(e) => setGiorno(e.target.value)} />}
        <p className="text-xs text-[var(--muted)]">Ogni anno, alla data indicata, la stagione viene archiviata e i punti contest ripartono da zero.</p>
      </div>
    </Modal>
  );
}

function ArchivioModal({ items, onClose }) {
  return (
    <Modal title="Archivio stagioni" testid="archivio-modal" onClose={onClose} onSave={onClose} saveLabel="Chiudi" hideCancel>
      {(!items || items.length === 0) ? <p className="text-sm text-[var(--muted)]">Nessuna stagione archiviata.</p> : (
        <div className="space-y-3">
          {items.map((a) => (
            <div key={a.id} data-testid={`archivio-${a.id}`} className="rounded-xl border border-[var(--border)] p-3">
              <div className="flex items-center justify-between text-xs text-[var(--muted)]"><span>{a.season_start || "inizio"} → {a.season_end}</span><span>{a.by}</span></div>
              {a.vincitore ? <div className="font-semibold text-sm mt-1 inline-flex items-center gap-1.5"><Crown size={14} className="text-amber-500" /> {a.vincitore.nome} · {ptIt(a.vincitore.punti_totali)} pt</div> : <div className="text-sm text-[var(--muted)] mt-1">Nessun punto</div>}
              {a.classifica?.length > 1 && <div className="text-xs text-[var(--muted)] mt-1">{a.classifica.length} agenti a punti</div>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

function ObiettivoEditModal({ o, onClose, onSaved }) {
  const [f, setF] = useState({ obiettivo_mese: o.obiettivo_mese, obiettivo_anno: o.obiettivo_anno, obiettivo_punti: o.obiettivo_punti ?? 10 });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/obiettivi/${o.agent_id}`, { obiettivo_mese: Number(f.obiettivo_mese) || 0, obiettivo_anno: Number(f.obiettivo_anno) || 0, obiettivo_punti: String(f.obiettivo_punti ?? "").trim() || 0 });
      toast.success("Obiettivi aggiornati");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Modal title={`Obiettivi — ${o.nome}`} onClose={onClose} onSave={save} saving={saving} testid="obiettivo-edit-modal">
      <Row>
        <Field label="Obiettivo mese €" type="euro" value={f.obiettivo_mese} testid="obj-mese" onChange={(e) => setF({ ...f, obiettivo_mese: e.target.value })} />
        <Field label="Obiettivo anno €" type="euro" value={f.obiettivo_anno} testid="obj-anno" onChange={(e) => setF({ ...f, obiettivo_anno: e.target.value })} />
      </Row>
      <Field label="Obiettivo punti / mese (contest)" value={String(f.obiettivo_punti ?? "").replace(".", ",")} testid="obj-punti" onChange={(e) => setF({ ...f, obiettivo_punti: e.target.value })} />
    </Modal>
  );
}

function TrendModal({ agent, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/obiettivi/storico", { params: { agent_id: agent.agent_id } }).then((r) => setData(r.data)).catch(() => setData([])); }, [agent.agent_id]);
  return (
    <Modal title={`Trend obiettivi — ${agent.nome}`} onClose={onClose} onSave={onClose} saveLabel="Chiudi" testid="trend-modal">
      {!data ? <div className="skeleton h-64 rounded-xl" /> :
       data.length === 0 ? <p className="text-sm text-[var(--muted)] text-center py-8">Nessuno storico disponibile.</p> : (
        <div>
          <p className="text-sm text-slate-600 mb-3">Andamento mensile del fatturato rispetto all'obiettivo.</p>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#ECECEF" vertical={false} />
              <XAxis dataKey="mese" stroke="#6B7280" fontSize={11} />
              <YAxis stroke="#6B7280" fontSize={11} tickFormatter={(v) => `€${Math.round(v / 1000)}k`} />
              <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #ECECEF", borderRadius: 12, color: "#111111" }} formatter={(v) => euro(v)} />
              <Legend />
              <Line type="monotone" dataKey="fatturato" name="Fatturato" stroke="#FC82ED" strokeWidth={3} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="obiettivo" name="Obiettivo" stroke="#CBD5E1" strokeWidth={2} strokeDasharray="5 5" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </Modal>
  );
}

const Stat = ({ n, l }) => <div className="hidden sm:block"><div className="mono font-bold text-sm">{n}</div><div className="text-[10px] uppercase text-[var(--muted)]">{l}</div></div>;
